import axiosAPI from '../../utils/client/axiosAPI';
import { ResponseWithPagination, Pagination } from '../contracts';
import {
  OrderResponse,
  OrderCreatePayload,
  OrderUpdatePayload,
  OrderQueryParams,
  OrderProduct,
  DocumentOrder,
  DocumentDownloadType,
  LaravelPaginatedResponse,
  ManualOrderCreateResponse
} from './contracts';
import { User } from '../User/User';
import { Product } from '../Product/Product';
import Session from '../../utils/storage/ClientStorage';

/**
 * The Order class model.
 * It provides an object-oriented way to interact with order data and API endpoints.
 * Instances of this class represent a single order.
 */
export class Order {
  public readonly id: string;
  public readonly user?: User;
  public readonly number_of_order: string;
  public readonly status: string;
  public readonly note: string | null;
  public readonly full_name: string;
  public readonly type: 'individual' | 'company';
  public readonly company_name: string | null;
  public readonly company_tax_number: string | null;
  public readonly company_id_number: string | null;
  public readonly email: string;
  public readonly phone: string;
  public readonly state: string;
  public readonly city: string;
  public readonly address: string;
  public readonly zip: string;
  public readonly reservation_start: string;
  public readonly reservation_end: string;
  public readonly washing_fee: boolean;
  public readonly total_price: number;
  public readonly price_factor: number;
  public readonly products: Product[];
  public readonly productsPivot: OrderProduct[];
  public readonly created_at: string;
  public readonly updated_at: string;
  public readonly personal_id_number?: string;
  public readonly washing_fee_percentage: number;
  public readonly washing_fee_value: number;
  public readonly tax_rate: number;
  public readonly tax_value: number;

  public constructor(data: OrderResponse) {
    this.id = data.id;
    this.number_of_order = data.number_of_order;
    this.status = data.status;
    this.note = data.note;
    this.full_name = data.full_name;
    this.type = data.type;
    this.company_name = data.company_name;
    this.company_tax_number = data.company_tax_number;
    this.company_id_number = data.company_id_number;
    this.personal_id_number = data.personal_id_number;
    this.email = data.email;
    this.phone = data.phone;
    this.state = data.state;
    this.city = data.city;
    this.address = data.address;
    this.zip = data.zip;
    this.reservation_start = data.reservation_start;
    this.reservation_end = data.reservation_end;
    this.washing_fee = data.washing_fee;
    this.total_price = data.total_price;
    this.price_factor = data.price_factor;
    this.created_at = data.created_at;
    this.updated_at = data.updated_at;

    this.washing_fee = data.washing_fee;
    this.washing_fee_percentage = data.washing_fee_percentage;
    this.washing_fee_value = data.washing_fee_value;
    this.price_factor = data.price_factor;
    this.tax_rate = data.tax_rate;
    this.tax_value = data.tax_value;
    this.total_price = data.total_price;

    this.user = data.user ? new User(data.user) : undefined;
    this.products = data.products.map((p) => new Product(p));
    this.productsPivot = data.products;
  }

  public static async getAll(
    params?: OrderQueryParams
  ): Promise<ResponseWithPagination<Order>> {
    const response = await axiosAPI.get<LaravelPaginatedResponse>(
      'admin/orders',
      {
        params
      }
    );
    const laravelResponse = response.data;

    const pagination: Pagination = {
      current_page: laravelResponse.meta.current_page,
      last_page: laravelResponse.meta.last_page,
      size: laravelResponse.meta.per_page,
      total: laravelResponse.meta.total,
      count: laravelResponse.data.length
    };

    const orders = laravelResponse.data.map(
      (orderData) => new Order(orderData)
    );

    return {
      pagination,
      data: orders
    };
  }

  public static async getOne(id: string): Promise<Order> {
    const response = await axiosAPI.get<OrderResponse>(`/orders/${id}`);

    return new Order(response.data);
  }

  public static async create(payload: OrderCreatePayload): Promise<Order> {
    const response = await axiosAPI.post<{ data: OrderResponse }>(
      '/orders',
      payload
    );
    return new Order(response.data.data);
  }

  /**
   * Creates a new order via the admin-specific endpoint.
   * @param payload - The data needed to create the order.
   * @returns A promise that resolves to the admin-specific response.
   */
  public static async createManual(
    payload: any
  ): Promise<ManualOrderCreateResponse> {
    const response = await axiosAPI.post<ManualOrderCreateResponse>(
      'admin/orders/create',
      payload
    );
    return response.data;
  }

  public static async update(
    id: string,
    payload: OrderUpdatePayload
  ): Promise<Order> {
    // The API response is the order object directly, so we expect OrderResponse.
    const response = await axiosAPI.put<OrderResponse>(
      `admin/orders/${id}`,
      payload
    );
    // We pass the response data directly to the constructor.
    return new Order(response.data);
  }

  public static async delete(id: string): Promise<null> {
    const { data } = await axiosAPI.delete(`/admin/orders/${id}`);
    return data;
  }

  public static async calculatePriceFactor(
    startDate: string,
    endDate: string
  ): Promise<{ price_factor: number; start_date: string; end_date: string }> {
    const response = await axiosAPI.get('/calculate-price-factor', {
      params: { start_date: startDate, end_date: endDate }
    });
    return response.data;
  }
  /**
   * Fetch all documents for this specific order.
   */
  public async getDocuments(): Promise<DocumentOrder[]> {
    const response = await axiosAPI.get<DocumentOrder[]>(
      `/admin/order-documents`,
      {
        params: { order_id: this.id }
      }
    );
    return response.data;
  }

  /**
   * Regenerate a single document for this order.
   */
  public async regenerateDocument(id: number): Promise<DocumentOrder> {
    const response = await axiosAPI.post<{ document: DocumentOrder }>(
      // The URL now points directly to the document to be regenerated
      `order-documents/${id}/regenerate`
    );
    return response.data.document;
}


  /**
   * Downloads a document file by its ID.
   */
  public static async downloadDocument(
    docId: number,
    originalName: string
  ): Promise<void> {
    const response = await axiosAPI.get(`order-documents/${docId}/download`, {
      responseType: 'blob'
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', originalName);
    document.body.appendChild(link);
    link.click();
    link.remove();
  }
}
